import { indexToLatLon } from "./goesGrid.js";

export const HYBRID_GREEN = Object.freeze({ c01: 0.465, c02: 0.465, c03: 0.07 });
export const GEO_GAMMA = 2.2;
const DAY_ZENITH = 75;
const NIGHT_ZENITH = 92;

export const GOES_PRODUCTS = Object.freeze([
  { id: "geo", label: "GeoColor", kind: "geo" },
  // GeoColor at band 2's 0.5 km, built from the single-band files.
  { id: "geosr", label: "GeoColor SuperRes", kind: "geo" },
  { id: "1", label: "Blue", kind: "refl" },
  { id: "2", label: "Red", kind: "refl" },
  { id: "3", label: "Veggie", kind: "refl" },
  { id: "4", label: "Cirrus", kind: "refl" },
  { id: "5", label: "Snow/Ice", kind: "refl" },
  { id: "6", label: "Cloud particle", kind: "refl" },
  { id: "7", label: "Shortwave", kind: "ir" },
  { id: "8", label: "Upper water vapor", kind: "wv" },
  { id: "9", label: "Mid water vapor", kind: "wv" },
  { id: "10", label: "Low water vapor", kind: "wv" },
  { id: "11", label: "Cloud phase", kind: "ir" },
  { id: "12", label: "Ozone", kind: "ir" },
  { id: "13", label: "Clean IR", kind: "ir" },
  { id: "14", label: "IR", kind: "ir" },
  { id: "15", label: "Dirty IR", kind: "ir" },
  { id: "16", label: "CO2", kind: "ir" },
]);

const PRODUCT_BY_ID = new Map(GOES_PRODUCTS.map((item) => [item.id, item]));

export function normalizeSatParam(value) {
  if (value == null || value === "") return null;
  const text = String(value).trim().toLowerCase();
  if (text === "geo" || text === "geocolor") return "geo";
  if (text === "geosr") return "geosr";
  const band = parseInt(text, 10);
  if (band >= 1 && band <= 16 && String(band) === text) return String(band);
  return null;
}

export function goesProduct(id) {
  return PRODUCT_BY_ID.get(normalizeSatParam(id) || "") || null;
}

export function bandsForProduct(id) {
  const product = goesProduct(id);
  if (!product) return [];
  if (product.kind === "geo") return [1, 2, 3, 13];
  return [parseInt(product.id, 10)];
}

export function bandDatasetName(band) {
  return `CMI_C${String(band).padStart(2, "0")}`;
}

export function hybridGreen(c01, c02, c03) {
  return (HYBRID_GREEN.c01 * c01) + (HYBRID_GREEN.c02 * c02) + (HYBRID_GREEN.c03 * c03);
}

export function gammaReflectance(value, gamma = GEO_GAMMA) {
  if (!Number.isFinite(value)) return null;
  const clipped = Math.max(0, Math.min(1, value));
  return clipped ** (1 / gamma);
}

export function dayWeight(zenithDeg) {
  if (!Number.isFinite(zenithDeg)) return 1;
  if (zenithDeg <= DAY_ZENITH) return 1;
  if (zenithDeg >= NIGHT_ZENITH) return 0;
  return (NIGHT_ZENITH - zenithDeg) / (NIGHT_ZENITH - DAY_ZENITH);
}

export function solarZenithDeg(lat, lon, timeMs) {
  if (!Number.isFinite(lat) || !Number.isFinite(lon) || !Number.isFinite(timeMs)) return null;
  const date = new Date(timeMs);
  const start = Date.UTC(date.getUTCFullYear(), 0, 0);
  const day = (timeMs - start) / 86400000;
  const hour = date.getUTCHours() + (date.getUTCMinutes() / 60) + (date.getUTCSeconds() / 3600);
  const gamma = (2 * Math.PI / 365) * (day - 1 + ((hour - 12) / 24));
  const eqtime = 229.18 * (
    0.000075
    + (0.001868 * Math.cos(gamma))
    - (0.032077 * Math.sin(gamma))
    - (0.014615 * Math.cos(2 * gamma))
    - (0.040849 * Math.sin(2 * gamma))
  );
  const decl = (
    0.006918
    - (0.399912 * Math.cos(gamma))
    + (0.070257 * Math.sin(gamma))
    - (0.006758 * Math.cos(2 * gamma))
    + (0.000907 * Math.sin(2 * gamma))
    - (0.002697 * Math.cos(3 * gamma))
    + (0.00148 * Math.sin(3 * gamma))
  );
  const tst = (hour * 60) + eqtime + (4 * lon);
  const ha = (((tst / 4) - 180) * Math.PI) / 180;
  const latR = (lat * Math.PI) / 180;
  const cosZen = (Math.sin(latR) * Math.sin(decl)) + (Math.cos(latR) * Math.cos(decl) * Math.cos(ha));
  return (Math.acos(Math.max(-1, Math.min(1, cosZen))) * 180) / Math.PI;
}

function lerp(a, b, t) {
  return a + ((b - a) * t);
}

function sampleStops(stops, value) {
  if (!Number.isFinite(value)) return null;
  if (value <= stops[0][0]) return stops[0].slice(1);
  const last = stops[stops.length - 1];
  if (value >= last[0]) return last.slice(1);
  for (let i = 1; i < stops.length; i++) {
    const right = stops[i];
    const left = stops[i - 1];
    if (value > right[0]) continue;
    const t = (value - left[0]) / (right[0] - left[0]);
    return [
      Math.round(lerp(left[1], right[1], t)),
      Math.round(lerp(left[2], right[2], t)),
      Math.round(lerp(left[3], right[3], t)),
    ];
  }
  return last.slice(1);
}

const IR_STOPS = [
  [180, 180, 40, 160],
  [200, 255, 80, 40],
  [220, 255, 220, 80],
  [230, 255, 255, 255],
  [250, 200, 200, 210],
  [280, 90, 90, 100],
  [330, 20, 20, 28],
];

const WV_STOPS = [
  [200, 255, 255, 255],
  [220, 200, 210, 230],
  [240, 80, 90, 120],
  [270, 10, 10, 20],
];

export function colorInfrared(kelvin) {
  const rgb = sampleStops(IR_STOPS, kelvin);
  return rgb ? [rgb[0], rgb[1], rgb[2], 255] : [0, 0, 0, 0];
}

export function colorWaterVapor(kelvin) {
  const rgb = sampleStops(WV_STOPS, kelvin);
  return rgb ? [rgb[0], rgb[1], rgb[2], 255] : [0, 0, 0, 0];
}

export function colorReflectance(value) {
  const g = gammaReflectance(value);
  if (g == null) return [0, 0, 0, 0];
  const byte = Math.round(g * 255);
  return [byte, byte, byte, 255];
}

export function geoColorRgba(c01, c02, c03, c13, zenithDeg) {
  const weight = dayWeight(zenithDeg);
  const red = gammaReflectance(c02);
  const green = Number.isFinite(c01) && Number.isFinite(c02) && Number.isFinite(c03)
    ? gammaReflectance(hybridGreen(c01, c02, c03))
    : null;
  const blue = gammaReflectance(c01);
  const dayOk = red != null && green != null && blue != null;
  const night = colorInfrared(c13);
  const nightOk = night[3] > 0;
  const dayW = dayOk ? weight : 0;
  const nightW = nightOk ? (1 - weight) : 0;
  const sum = dayW + nightW;
  if (!(sum > 0)) return [0, 0, 0, 0];
  const dayRgb = dayOk
    ? [red * 255, green * 255, blue * 255]
    : [0, 0, 0];
  return [
    Math.round(((dayRgb[0] * dayW) + (night[0] * nightW)) / sum),
    Math.round(((dayRgb[1] * dayW) + (night[1] * nightW)) / sum),
    Math.round(((dayRgb[2] * dayW) + (night[2] * nightW)) / sum),
    255,
  ];
}

export function unpackCmi(raw, scale, offset, fill, unsigned) {
  const out = new Float32Array(raw.length);
  const fillValue = Number(fill);
  const scaleN = Number(scale);
  const offsetN = Number(offset);
  const isUnsigned = unsigned === true || String(unsigned).toLowerCase() === "true";
  for (let i = 0; i < raw.length; i++) {
    const sample = raw[i];
    if (sample === fillValue || !Number.isFinite(sample)) {
      out[i] = NaN;
      continue;
    }
    let value = sample;
    if (isUnsigned && value < 0) value += 65536;
    out[i] = (value * scaleN) + offsetN;
  }
  return out;
}

function pixelValue(grid, index) {
  if (!grid) return NaN;
  const value = grid[index];
  return Number.isFinite(value) ? value : NaN;
}

// The sun angle changes slowly across a crop, so it is worked out every
// ZENITH_STEP cells and interpolated between.
const ZENITH_STEP = 8;

function zenithAt(ix, iy, proj, timeMs) {
  const ll = indexToLatLon(ix, iy, proj);
  return ll ? solarZenithDeg(ll.lat, ll.lon, timeMs) : null;
}

export function zenithGrid({ width, height, x0, y0, stride = 1, proj, timeMs, step = ZENITH_STEP }) {
  const out = new Float32Array(width * height);
  if (!Number.isFinite(timeMs) || !(width > 0) || !(height > 0)) return out;
  const gw = Math.ceil((width - 1) / step) + 1;
  const gh = Math.ceil((height - 1) / step) + 1;
  const knots = new Float64Array(gw * gh);
  for (let gy = 0; gy < gh; gy++) {
    for (let gx = 0; gx < gw; gx++) {
      const col = Math.min(width - 1, gx * step);
      const row = Math.min(height - 1, gy * step);
      knots[(gy * gw) + gx] = zenithAt(x0 + (col * stride), y0 + (row * stride), proj, timeMs) ?? NaN;
    }
  }
  for (let row = 0; row < height; row++) {
    const gy = Math.min(gh - 2, Math.floor(row / step));
    const ry0 = gy * step;
    const ry1 = Math.min(height - 1, (gy + 1) * step);
    const fy = gh > 1 && ry1 > ry0 ? (row - ry0) / (ry1 - ry0) : 0;
    for (let col = 0; col < width; col++) {
      const index = (row * width) + col;
      const gx = Math.min(gw - 2, Math.floor(col / step));
      const k = (gy * gw) + gx;
      const z00 = knots[k];
      const z01 = knots[k + 1];
      const z10 = knots[k + gw];
      const z11 = knots[k + gw + 1];
      // Near the Earth's limb a lattice point can miss the globe; work those
      // cells out one by one.
      if (gw < 2 || gh < 2 || !(Number.isFinite(z00) && Number.isFinite(z01) && Number.isFinite(z10) && Number.isFinite(z11))) {
        out[index] = zenithAt(x0 + (col * stride), y0 + (row * stride), proj, timeMs) ?? 0;
        continue;
      }
      const rx0 = gx * step;
      const rx1 = Math.min(width - 1, (gx + 1) * step);
      const fx = rx1 > rx0 ? (col - rx0) / (rx1 - rx0) : 0;
      const top = z00 + ((z01 - z00) * fx);
      const bottom = z10 + ((z11 - z10) * fx);
      out[index] = top + ((bottom - top) * fy);
    }
  }
  return out;
}

export function colorizeCrop({ product, grids, width, height, y0, x0, stride = 1, proj, timeMs }) {
  const id = goesProduct(product)?.id || "geo";
  const spec = goesProduct(id);
  const rgba = new Uint8ClampedArray(width * height * 4);
  const geo = spec.kind === "geo";
  const zenith = geo ? zenithGrid({ width, height, x0, y0, stride, proj, timeMs }) : null;
  for (let row = 0; row < height; row++) {
    for (let col = 0; col < width; col++) {
      const index = (row * width) + col;
      const dest = index * 4;
      let rgbaPx;
      if (geo) {
        rgbaPx = geoColorRgba(
          pixelValue(grids[1], index),
          pixelValue(grids[2], index),
          pixelValue(grids[3], index),
          pixelValue(grids[13], index),
          zenith[index],
        );
      } else if (spec.kind === "wv") {
        rgbaPx = colorWaterVapor(pixelValue(grids[parseInt(id, 10)], index));
      } else if (spec.kind === "ir") {
        rgbaPx = colorInfrared(pixelValue(grids[parseInt(id, 10)], index));
      } else {
        rgbaPx = colorReflectance(pixelValue(grids[parseInt(id, 10)], index));
      }
      rgba[dest] = rgbaPx[0];
      rgba[dest + 1] = rgbaPx[1];
      rgba[dest + 2] = rgbaPx[2];
      rgba[dest + 3] = rgbaPx[3];
    }
  }
  return rgba;
}

export function formatGoesClock(ms) {
  if (!Number.isFinite(ms)) return "";
  const dt = new Date(ms);
  const hh = String(dt.getUTCHours()).padStart(2, "0");
  const mm = String(dt.getUTCMinutes()).padStart(2, "0");
  return `${hh}:${mm}Z`;
}

export function goesInfoLabel({ loading = false, sat = "", product = "geo", timeMs = null } = {}) {
  const clock = formatGoesClock(timeMs);
  const id = sat || "";
  if (loading && !id && !clock) return "loading";
  const name = goesProduct(product)?.label || "GeoColor";
  const text = [id, name, clock].filter(Boolean).join(" ");
  return loading ? `${text}…` : text;
}

const KELVIN_OFFSET = 273.15;
const LEGEND_RANGE_C = Object.freeze({ ir: [-90, 30], wv: [-80, 0] });

// Brightness-temperature colour bar for the IR and water vapour bands, sampled
// every `stepC` degrees C from cold to warm. Null for GeoColor and visible bands.
export function goesLegendStops(product, stepC = 5) {
  const spec = goesProduct(product);
  const range = spec && LEGEND_RANGE_C[spec.kind];
  if (!range) return null;
  const stops = spec.kind === "wv" ? WV_STOPS : IR_STOPS;
  const [minC, maxC] = range;
  const step = stepC > 0 ? stepC : 5;
  const colors = [];
  for (let c = minC; c <= maxC + 1e-9; c += step) {
    colors.push({ c, rgb: sampleStops(stops, c + KELVIN_OFFSET) });
  }
  return { kind: spec.kind, minC, maxC, colors };
}
